import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { MapPin, Calendar, Clock, Ticket, Loader2, Plane, Hotel, Plus, X, Star, ChevronDown, ChevronUp } from "lucide-react";
import { format, addDays, isValid, parseISO } from "date-fns";
import { toast } from "sonner";
import Footer from "../components/Footer";
import CityAutocomplete from "../components/CityAutocomplete";
import LiveVibeCounter from "../components/LiveVibeCounter";
import StickySearchHeader from "../components/StickySearchHeader";
import { haptic } from "../utils/haptic";
import { useAudio } from "../contexts/AudioContext";
import { AIInsightsRow, AISummaryBadge } from "../components/AILogisticsBadge";
import { ShareButton, ViralReferralBanner } from "../components/SocialShare";

// Import modular booking components
import { EarlyArrivalRecoveryBlock, TripRoadmapOverlay } from "../components/booking";

// Safe date parser - prevents RangeError crashes
const safeParseDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
    return isValid(date) ? date : null;
  } catch {
    return null;
  }
};

// Safe date formatter - returns fallback if date is invalid
const safeFormat = (date, formatStr, fallback = "TBC") => {
  if (!date || !isValid(date)) return fallback;
  try {
    return format(date, formatStr);
  } catch {
    return fallback;
  }
};

// Skeleton shimmer component for loading states
const SkeletonShimmer = ({ className = "" }) => (
  <div className={`skeleton ${className}`} />
);

// Accordion reveal component with 300ms slide transition
const AccordionReveal = ({ isOpen, children }) => (
  <div
    className="overflow-hidden transition-all duration-300 ease-in-out"
    style={{
      maxHeight: isOpen ? '500px' : '0',
      opacity: isOpen ? 1 : 0,
      transform: isOpen ? 'translateY(0)' : 'translateY(-8px)',
    }}
  >
    {children}
  </div>
);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency formatting
const formatPrice = (price, currency = "GBP") => {
  const symbol = currency === "GBP" ? "£" : "€";
  return `${symbol}${Math.round(price)}`;
};

// City to IATA mapping
const CITY_IATA_MAP = {
  "london": "LHR", "berlin": "BER", "amsterdam": "AMS", "paris": "CDG",
  "barcelona": "BCN", "ibiza": "IBZ", "manchester": "MAN", "brussels": "BRU",
  "mannheim": "FRA", "munich": "MUC", "frankfurt": "FRA"
};

// Commission rates by tier
const COMMISSION_RATES = {
  ticketOnly: 0.10,
  ticketFlight: 0.15,
  completeTrip: 0.20
};

const EventDetail = ({ onSignUpClick }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { stopAll } = useAudio(); // Audio singleton - stop on proceed/checkout
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tripLoading, setTripLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  // Trip bundle state
  const [originCity, setOriginCity] = useState("");
  const [originIata, setOriginIata] = useState("LHR");
  const [tripBundle, setTripBundle] = useState(null);
  const [guests, setGuests] = useState(1);
  const [hotelClass, setHotelClass] = useState("mid");
  const [geoLoading, setGeoLoading] = useState(true);

  // Overlay state
  const [showOverlay, setShowOverlay] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  
  // Expanded tier state for accordion reveal
  const [expandedTier, setExpandedTier] = useState(null);

  // Fetch event
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/events/${eventId}`);
        if (!response.ok) throw new Error("Event not found");
        const data = await response.json();
        setEvent(data);
      } catch (error) {
        toast.error("Event not found");
        navigate("/events");
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId, navigate]);

  // Detect user location
  useEffect(() => {
    const detectLocation = async () => {
      setGeoLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/trip/geo-detect`);
        if (response.ok) {
          const data = await response.json();
          setOriginCity(data.city || "London");
          setOriginIata(data.iata || "LHR");
        }
      } catch {
        setOriginCity("London");
        setOriginIata("LHR");
      }
      setGeoLoading(false);
    };
    detectLocation();
  }, []);

  // Handle city selection from autocomplete
  const handleCitySelect = (city, iata) => {
    setOriginCity(city);
    setOriginIata(iata);
  };

  // Search trip bundle
  const searchTripBundle = useCallback(async () => {
    if (!event || !originCity || geoLoading) return;
    
    const eventDate = safeParseDate(event.start_at);
    if (!eventDate) {
      console.error("Invalid event date");
      return;
    }
    
    setTripLoading(true);
    try {
      const checkIn = format(addDays(eventDate, -1), "yyyy-MM-dd");
      const checkOut = format(addDays(eventDate, 1), "yyyy-MM-dd");
      
      const response = await fetch(`${API_URL}/api/trip/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_id: eventId,
          origin_city: originCity,
          origin_iata: originIata,
          check_in: checkIn,
          check_out: checkOut,
          travellers: guests,
          hotel_class: hotelClass,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setTripBundle(data);
      }
    } catch (error) {
      console.error("Trip search error:", error);
    } finally {
      setTripLoading(false);
    }
  }, [event, originCity, originIata, eventId, guests, hotelClass, geoLoading]);

  useEffect(() => {
    if (event && originCity && !geoLoading) {
      const debounce = setTimeout(searchTripBundle, 500);
      return () => clearTimeout(debounce);
    }
  }, [event, originCity, originIata, guests, hotelClass, geoLoading, searchTripBundle]);

  // Calculate prices
  const calculatePrices = () => {
    const baseTicket = (event?.ticket_price || 0) * guests;
    const baseFlight = tripBundle?.flight?.price || 0;
    const baseHotel = tripBundle?.hotel?.price || 0;

    return {
      ticketOnly: { total: Math.round(baseTicket * (1 + COMMISSION_RATES.ticketOnly)) },
      ticketFlight: { total: Math.round((baseTicket + baseFlight) * (1 + COMMISSION_RATES.ticketFlight)) },
      completeTrip: { total: Math.round((baseTicket + baseFlight + baseHotel) * (1 + COMMISSION_RATES.completeTrip)) }
    };
  };

  const prices = calculatePrices();

  // Handle tier expansion click (15ms haptic pulse)
  const handleTierExpand = (tier) => {
    haptic(15); // 15ms haptic pulse as per requirement
    setExpandedTier(expandedTier === tier ? null : tier);
  };

  // Handle book click (proceeds to overlay)
  const handleBookClick = (tier) => {
    stopAll(); // Audio singleton - stop when booking starts
    haptic("medium"); // 25ms haptic on booking
    setSelectedTier(tier);
    setShowOverlay(true);
  };

  // Proceed to payment
  const handleProceedToPayment = async () => {
    if (!event || !selectedTier) return;
    stopAll(); // Audio singleton - complete silence before payment
    haptic("strong"); // Confirm before payment
    setCheckoutLoading(true);
    
    const tierPrices = prices[selectedTier];
    let description = "";

    if (selectedTier === "ticketOnly") {
      description = `${event.name} - ${guests} ticket(s)`;
    } else if (selectedTier === "ticketFlight") {
      description = `${event.name} + Flight from ${originCity}`;
    } else {
      description = `Complete Trip: ${event.name} + Flight + Hotel`;
    }

    try {
      const response = await fetch(`${API_URL}/api/checkout/create-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_id: event.event_id,
          event_name: description,
          quantity: 1,
          unit_price: tierPrices.total,
          currency: "gbp",
          success_url: `${window.location.origin}/booking-success?session_id={CHECKOUT_SESSION_ID}&tier=${selectedTier}`,
          cancel_url: window.location.href,
          metadata: { tier: selectedTier, guests: String(guests), origin: originCity }
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        toast.error("Failed to create checkout");
        setCheckoutLoading(false);
      }
    } catch (error) {
      toast.error("Checkout failed");
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen animated-bg pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full glass flex items-center justify-center bpm-pulse">
            <Ticket className="w-8 h-8 text-primary" />
          </div>
          <p className="font-mono text-muted-foreground">Loading experience...</p>
        </div>
      </div>
    );
  }

  if (!event) return null;

  // Parse dates safely - with fallback loading state if dates are invalid
  const startDate = safeParseDate(event.start_at);
  const endDate = safeParseDate(event.end_at) || startDate;
  
  // If critical event data is missing, show loading state
  if (!startDate) {
    return (
      <div className="min-h-screen animated-bg pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full glass flex items-center justify-center bpm-pulse">
            <Ticket className="w-8 h-8 text-primary" />
          </div>
          <p className="font-mono text-muted-foreground">Loading Manifest...</p>
        </div>
      </div>
    );
  }

  // Event context for sticky header
  const eventContext = {
    name: event.name,
    city: event.venue_city,
    date: safeFormat(startDate, "MMM dd", "TBC")
  };

  return (
    <div className="min-h-screen animated-bg" style={{ paddingTop: '64px' }}>
      {/* Sticky Search Header with Event Context */}
      <StickySearchHeader scrollThreshold={300} eventContext={eventContext} />

      {/* Hero */}
      <div className="relative h-[35vh] min-h-[280px] overflow-hidden">
        <img 
          src={event.image_url || "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1920"} 
          alt={event.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="absolute top-8 left-4 sm:left-8">
          <span className="glass-solid px-4 py-2 font-mono text-sm font-bold text-primary uppercase tracking-wider rounded-full">
            {event.genre}
          </span>
        </div>
        {/* Share Button */}
        <div className="absolute top-8 right-4 sm:right-8">
          <ShareButton event={event} variant="icon" />
        </div>
      </div>

      {/* Trip Dashboard - Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative pb-16" style={{ zIndex: 10 }}>
        {/* Event Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-3">
              {event.name}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-muted-foreground text-sm">
            <span className="flex items-center gap-2 font-mono">
              <Calendar className="w-4 h-4 text-primary" />
              {safeFormat(startDate, "MMM dd, yyyy")}
            </span>
            <span className="flex items-center gap-2 font-mono">
              <MapPin className="w-4 h-4 text-accent" />
              {event.venue_city}, {event.venue_country}
            </span>
          </div>
        </div>

        {/* Search Controls */}
        <div className="glass rounded-2xl p-6 mb-8" style={{ position: 'relative', zIndex: 100 }}>
          <div className="grid sm:grid-cols-3 gap-4">
            {/* Origin City Autocomplete */}
            <div className="relative" style={{ zIndex: 200 }}>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block font-mono">
                Flying From
              </label>
              <CityAutocomplete
                value={originCity}
                onChange={setOriginCity}
                onSelect={handleCitySelect}
                placeholder="Search airports..."
              />
            </div>

            {/* Guests */}
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block font-mono">
                Guests
              </label>
              <select
                value={guests}
                onChange={(e) => setGuests(Number(e.target.value))}
                className="w-full h-12 glass rounded-lg px-4 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {[1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? "Guest" : "Guests"}</option>
                ))}
              </select>
            </div>

            {/* Hotel Class */}
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block font-mono">
                Hotel Class
              </label>
              <div className="flex gap-2 h-12">
                {["budget", "mid", "luxury"].map((cls) => (
                  <button
                    key={cls}
                    onClick={() => setHotelClass(cls)}
                    className={`flex-1 rounded-lg font-mono text-xs uppercase tracking-wider transition-all ${
                      hotelClass === cls
                        ? "glass bg-accent/20 text-accent border-accent"
                        : "glass text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {cls}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Live Progress - Uber Map Effect */}
          {tripLoading && (
            <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground font-mono animate-pulse">
              <Loader2 className="w-3 h-3 animate-spin text-primary" />
              <span>Scanning 14 airlines and 86 hotels to find the path of least resistance...</span>
            </div>
          )}
        </div>

        {/* Price Pillars */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {/* Event */}
          <div className="glass rounded-2xl p-6 border-2 border-primary/50">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                <Ticket className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>Event</div>
                <div className="font-heading font-bold text-foreground truncate">{event.name}</div>
              </div>
            </div>
            <div className="text-3xl font-black text-primary" style={{ fontFamily: "'Space Mono', monospace" }}>
              {formatPrice((event.ticket_price || 0) * guests)}
            </div>
            <div className="text-xs text-muted-foreground mt-1" style={{ fontFamily: "'Space Mono', monospace" }}>
              {guests} × {formatPrice(event.ticket_price || 0)}
            </div>
          </div>

          {/* Flight */}
          <div className={`glass rounded-2xl p-6 ${tripBundle?.flight ? "border-2 border-secondary/50" : ""}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center">
                <Plane className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>Flight</div>
                <div className="font-heading font-bold text-foreground">
                  {tripBundle?.flight?.airline || "Searching..."}
                </div>
              </div>
            </div>
            {tripLoading ? (
              <Loader2 className="w-6 h-6 text-secondary animate-spin" />
            ) : tripBundle?.flight ? (
              <>
                <div className="text-3xl font-black text-secondary" style={{ fontFamily: "'Space Mono', monospace" }}>
                  {formatPrice(tripBundle.flight.price)}
                </div>
                {/* Round-trip route display */}
                <div className="text-xs text-muted-foreground mt-1" style={{ fontFamily: "'Space Mono', monospace" }}>
                  {originIata} ⇄ {CITY_IATA_MAP[event.venue_city?.toLowerCase()] || "---"} • Round-Trip
                </div>
                {/* OUT leg */}
                {tripBundle.flight.outbound && (
                  <div className="text-[10px] text-muted-foreground/80 mt-2" style={{ fontFamily: "'Space Mono', monospace" }}>
                    <span className="text-secondary">OUT:</span> {tripBundle.flight.outbound.departure_time || '--:--'} → {tripBundle.flight.outbound.arrival_time || '--:--'}
                  </div>
                )}
                {/* RET leg */}
                {tripBundle.flight.return && (
                  <div className="text-[10px] text-muted-foreground/80" style={{ fontFamily: "'Space Mono', monospace" }}>
                    <span className="text-secondary">RET:</span> {tripBundle.flight.return.departure_time || '--:--'} → {tripBundle.flight.return.arrival_time || '--:--'}
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted-foreground text-sm">Same city</div>
            )}
          </div>

          {/* Hotel */}
          <div className={`glass rounded-2xl p-6 ${tripBundle?.hotel ? "border-2 border-accent/50" : ""}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
                <Hotel className="w-6 h-6 text-accent" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-muted-foreground uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>Hotel</div>
                <div className="font-heading font-bold text-foreground truncate">
                  {tripBundle?.hotel?.name || hotelClass.charAt(0).toUpperCase() + hotelClass.slice(1)}
                </div>
              </div>
              {/* Live Availability Badge */}
              {tripBundle?.hotel && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-accent/20 text-accent text-[10px]" style={{ fontFamily: "'Space Mono', monospace" }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                  LIVE
                </div>
              )}
            </div>
            {tripLoading ? (
              <div className="space-y-2">
                <SkeletonShimmer className="h-9 w-24" />
                <SkeletonShimmer className="h-4 w-32" />
              </div>
            ) : tripBundle?.hotel ? (
              <>
                <div 
                  className="text-3xl font-black text-accent"
                  style={{ fontFamily: "'Space Mono', monospace" }}
                >
                  {formatPrice(tripBundle.hotel.price)}
                </div>
                {/* Star Rating */}
                {tripBundle.hotel.star_rating && (
                  <div className="flex items-center gap-1 mt-1">
                    {[...Array(tripBundle.hotel.star_rating)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                )}
                <div className="text-xs text-muted-foreground mt-1" style={{ fontFamily: "'Space Mono', monospace" }}>
                  {tripBundle.hotel.nights} nights • {tripBundle.hotel.late_checkout ? "Late checkout ✓" : tripBundle.hotel.checkout_time || "Near venue"}
                </div>
                {/* Check-in Time - Important for roadmap alignment */}
                {tripBundle.hotel.checkin_time && (
                  <div className="text-xs mt-1" style={{ fontFamily: "'Space Mono', monospace" }}>
                    <span className="text-muted-foreground">Check-in: </span>
                    <span className={tripBundle.hotel.early_checkin ? "text-cyan-400" : "text-foreground"}>
                      {tripBundle.hotel.checkin_time}
                      {tripBundle.hotel.early_checkin && " (early)"}
                    </span>
                  </div>
                )}
                {/* Proximity Badge */}
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded bg-accent/10 text-accent" style={{ fontFamily: "'Space Mono', monospace" }}>
                    {tripBundle.hotel.distance_km ? `${tripBundle.hotel.distance_km}km from venue` : "<2km from venue"}
                  </span>
                </div>
                {/* AI Logistics Insights */}
                {tripBundle.ai_insights && tripBundle.ai_insights.length > 0 && (
                  <AIInsightsRow insights={tripBundle.ai_insights} maxDisplay={2} />
                )}
                {/* AI Summary Badge */}
                {tripBundle.ai_summary_badge && (
                  <AISummaryBadge label={tripBundle.ai_summary_badge} className="mt-1" />
                )}
              </>
            ) : (
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            )}
          </div>
        </div>

        {/* EARLY ARRIVAL RECOVERY BLOCK - Dead Zone Fix */}
        {tripBundle?.roadmap_notes && tripBundle.roadmap_notes.some(n => n.type === "early_arrival_recovery") && (
          <EarlyArrivalRecoveryBlock 
            note={tripBundle.roadmap_notes.find(n => n.type === "early_arrival_recovery")}
            hotelCheckinTime={tripBundle.hotel?.checkin_time || "15:00"}
          />
        )}

        {/* Standard Journey Note - For non-critical timing */}
        {tripBundle?.roadmap_notes && tripBundle.roadmap_notes.length > 0 && 
         !tripBundle.roadmap_notes.some(n => n.type === "early_arrival_recovery") && (
          <div className="mb-6 p-4 rounded-xl border-2 border-cyan-400/30 bg-cyan-400/5" style={{ boxShadow: '0 0 15px rgba(34, 211, 238, 0.1)' }}>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-cyan-400/20 flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 text-cyan-400" />
              </div>
              <div>
                <div className="text-sm font-bold text-cyan-400 mb-1" style={{ fontFamily: "'Space Mono', monospace" }}>
                  Journey Note
                </div>
                <div className="text-xs text-muted-foreground" style={{ fontFamily: "'Space Mono', monospace" }}>
                  {tripBundle.roadmap_notes[0].message}
                </div>
                <div className="text-xs text-foreground/70 mt-1 italic">
                  {tripBundle.roadmap_notes[0].recommendation}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Live Vibe Counter - Social Proof */}
        <div className="mb-6">
          <LiveVibeCounter eventId={event?.event_id} eventName={event?.name} />
        </div>

        {/* Viral Referral Banner - Now Functional */}
        <ViralReferralBanner event={event} className="mb-6" />

        {/* Experience Tiers - Redesigned with Techno Typography & Accordion Reveal */}
        <div className="glass rounded-2xl p-6 mb-8">
          {/* Header with Space Mono typography */}
          <div className="flex items-center justify-between mb-2">
            <div 
              className="text-xs text-muted-foreground uppercase tracking-wider"
              style={{ fontFamily: "'Space Mono', monospace" }}
            >
              Choose Your Experience
            </div>
          </div>
          
          {/* Scarcity Signal */}
          <div 
            className="text-xs text-primary/80 mb-6 italic"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            Strictly limited to {((event?.event_id?.charCodeAt(4) || 7) % 9) + 3} spots this week
          </div>

          {/* Tier Cards Grid - Flexbox aligned */}
          <div className="grid sm:grid-cols-3 gap-5">
            
            {/* Tier 1: The Minimalist */}
            <div className="glass-solid rounded-xl border border-border hover:border-primary/50 transition-all duration-300 flex flex-col h-full">
              {/* Card Content */}
              <div className="p-5 flex-1 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <span 
                    className="text-[10px] uppercase tracking-widest text-muted-foreground"
                    style={{ fontFamily: "'Space Mono', monospace" }}
                  >
                    The Minimalist
                  </span>
                  <Ticket className="w-5 h-5 text-primary/60" />
                </div>
                
                {/* Price with neon glow effect */}
                <div 
                  className="text-3xl font-black text-foreground mb-3 text-center"
                  style={{ 
                    fontFamily: "'Space Mono', monospace",
                    textShadow: '0 0 20px rgba(255, 0, 0, 0.4)'
                  }}
                >
                  {tripLoading ? <SkeletonShimmer className="h-9 w-24 mx-auto" /> : formatPrice(prices.ticketOnly.total)}
                </div>
                
                {/* Description */}
                <p className="text-[11px] text-muted-foreground leading-relaxed text-center flex-1">
                  For the purist who values the dancefloor above all else. No fluff, just access. We save you the 4-hour RA queue stress.
                </p>
              </div>
              
              {/* CTA Button - Always at bottom */}
              <div className="p-5 pt-0">
                <Button
                  onClick={() => handleBookClick("ticketOnly")}
                  disabled={checkoutLoading}
                  variant="outline"
                  className="w-full h-12 border-primary/30 hover:border-primary hover:bg-primary/10 transition-all"
                  data-testid="tier-minimalist-cta"
                >
                  <span style={{ fontFamily: "'Space Mono', monospace" }} className="text-xs uppercase tracking-wider">
                    Select
                  </span>
                </Button>
              </div>
            </div>

            {/* Tier 2: The Efficiency Play */}
            <div className="glass-solid rounded-xl border-2 border-secondary/30 hover:border-secondary/60 bg-secondary/5 transition-all duration-300 flex flex-col h-full">
              {/* Card Content */}
              <div className="p-5 flex-1 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <span 
                    className="text-[10px] uppercase tracking-widest text-secondary"
                    style={{ fontFamily: "'Space Mono', monospace" }}
                  >
                    The Efficiency Play
                  </span>
                  <div className="flex items-center gap-0.5">
                    <Ticket className="w-4 h-4 text-secondary/80" />
                    <Plus className="w-3 h-3 text-secondary/60" />
                    <Plane className="w-4 h-4 text-secondary/80" />
                  </div>
                </div>
                
                {/* Price with neon glow */}
                <div 
                  className="text-3xl font-black text-foreground mb-3 text-center"
                  style={{ 
                    fontFamily: "'Space Mono', monospace",
                    textShadow: '0 0 20px rgba(168, 85, 247, 0.4)'
                  }}
                >
                  {tripLoading ? <SkeletonShimmer className="h-9 w-24 mx-auto" /> : formatPrice(prices.ticketFlight.total)}
                </div>
                
                {/* Description */}
                <p className="text-[11px] text-muted-foreground leading-relaxed text-center mb-3">
                  The opposite of a bad journey is a forgettable one. Certainty you'll arrive before the first beat drops.
                </p>
                
                {/* Expand/Collapse Toggle for Flight Details */}
                <button
                  onClick={() => handleTierExpand("ticketFlight")}
                  className="flex items-center justify-center gap-1 text-[10px] text-secondary/70 hover:text-secondary transition-colors mx-auto"
                  style={{ fontFamily: "'Space Mono', monospace" }}
                  data-testid="tier-efficiency-expand"
                >
                  {expandedTier === "ticketFlight" ? (
                    <>Hide details <ChevronUp className="w-3 h-3" /></>
                  ) : (
                    <>View flight details <ChevronDown className="w-3 h-3" /></>
                  )}
                </button>
                
                {/* Accordion Reveal - Flight Details */}
                <AccordionReveal isOpen={expandedTier === "ticketFlight"}>
                  <div className="mt-3 p-3 rounded-lg bg-secondary/10 border border-secondary/20">
                    {tripBundle?.flight ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-secondary" style={{ fontFamily: "'Space Mono', monospace" }}>
                            {tripBundle.flight.airline || "Airline"}
                          </span>
                          <span className="text-[10px] text-muted-foreground" style={{ fontFamily: "'Space Mono', monospace" }}>
                            {formatPrice(tripBundle.flight.price)}
                          </span>
                        </div>
                        {/* ROUND-TRIP: OUT leg */}
                        <div className="text-[10px] text-muted-foreground" style={{ fontFamily: "'Space Mono', monospace" }}>
                          <span className="text-secondary font-bold">OUT:</span>{' '}
                          {tripBundle.flight.outbound?.carrier || ''}{tripBundle.flight.outbound?.flight_number ? ` ${tripBundle.flight.outbound.flight_number}` : ''} | {tripBundle.flight.outbound?.departure_time || '--:--'} → {tripBundle.flight.outbound?.arrival_time || '--:--'}
                        </div>
                        {/* ROUND-TRIP: RET leg */}
                        {tripBundle.flight.return && (
                          <div className="text-[10px] text-muted-foreground" style={{ fontFamily: "'Space Mono', monospace" }}>
                            <span className="text-secondary font-bold">RET:</span>{' '}
                            {tripBundle.flight.return?.carrier || ''}{tripBundle.flight.return?.flight_number ? ` ${tripBundle.flight.return.flight_number}` : ''} | {tripBundle.flight.return?.departure_time || '--:--'} → {tripBundle.flight.return?.arrival_time || '--:--'}
                          </div>
                        )}
                        <div className="text-[10px] text-muted-foreground/70" style={{ fontFamily: "'Space Mono', monospace" }}>
                          {originIata} ⇄ {CITY_IATA_MAP[event.venue_city?.toLowerCase()] || "---"}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <SkeletonShimmer className="h-3 w-full" />
                        <SkeletonShimmer className="h-3 w-2/3" />
                      </div>
                    )}
                  </div>
                </AccordionReveal>
              </div>
              
              {/* CTA Button - Always at bottom */}
              <div className="p-5 pt-0 mt-auto">
                <Button
                  onClick={() => handleBookClick("ticketFlight")}
                  disabled={checkoutLoading || !tripBundle?.flight}
                  className="w-full h-12 bg-secondary/20 hover:bg-secondary/30 border border-secondary/40 text-foreground transition-all"
                  data-testid="tier-efficiency-cta"
                >
                  <span style={{ fontFamily: "'Space Mono', monospace" }} className="text-xs uppercase tracking-wider">
                    Select
                  </span>
                </Button>
              </div>
            </div>

            {/* Tier 3: The Psychological Moonshot */}
            <div 
              className="rounded-xl bg-gradient-to-br from-primary via-secondary to-accent transition-all duration-300 flex flex-col h-full"
              style={{ 
                boxShadow: '0 0 30px rgba(255, 0, 0, 0.3)'
              }}
            >
              {/* Card Content */}
              <div className="p-5 flex-1 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <span 
                    className="text-[10px] uppercase tracking-widest text-white/90"
                    style={{ fontFamily: "'Space Mono', monospace" }}
                  >
                    The Psychological Moonshot
                  </span>
                  <div className="flex items-center gap-0.5">
                    <Ticket className="w-4 h-4 text-white/80" />
                    <Plus className="w-3 h-3 text-white/60" />
                    <Plane className="w-4 h-4 text-white/80" />
                    <Plus className="w-3 h-3 text-white/60" />
                    <Hotel className="w-4 h-4 text-white/80" />
                  </div>
                </div>
                
                {/* Price with enhanced neon glow */}
                <div 
                  className="text-4xl font-black text-white mb-3 text-center"
                  style={{ 
                    fontFamily: "'Space Mono', monospace",
                    textShadow: '0 0 30px rgba(255, 255, 255, 0.4)'
                  }}
                >
                  {tripLoading ? <SkeletonShimmer className="h-10 w-28 mx-auto bg-white/20" /> : formatPrice(prices.completeTrip.total)}
                </div>
                
                {/* Description */}
                <p className="text-[11px] text-white/80 leading-relaxed text-center mb-3">
                  Luxury isn't a bed; it's the removal of friction. Late checkout because the best part happens after 4AM.
                </p>
                
                {/* Expand/Collapse Toggle for Flight + Hotel Details */}
                <button
                  onClick={() => handleTierExpand("completeTrip")}
                  className="flex items-center justify-center gap-1 text-[10px] text-white/70 hover:text-white transition-colors mx-auto"
                  style={{ fontFamily: "'Space Mono', monospace" }}
                  data-testid="tier-moonshot-expand"
                >
                  {expandedTier === "completeTrip" ? (
                    <>Hide details <ChevronUp className="w-3 h-3" /></>
                  ) : (
                    <>View full package <ChevronDown className="w-3 h-3" /></>
                  )}
                </button>
                
                {/* Accordion Reveal - Flight + Hotel Details */}
                <AccordionReveal isOpen={expandedTier === "completeTrip"}>
                  <div className="mt-3 space-y-2">
                    {/* Flight Details */}
                    <div className="p-3 rounded-lg bg-white/10 border border-white/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Plane className="w-3 h-3 text-white/70" />
                        <span className="text-[10px] text-white/70 uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>
                          Round-Trip Flight
                        </span>
                      </div>
                      {tripBundle?.flight ? (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-white" style={{ fontFamily: "'Space Mono', monospace" }}>
                              {tripBundle.flight.airline || "Airline"}
                            </span>
                            <span className="text-[10px] text-white/70" style={{ fontFamily: "'Space Mono', monospace" }}>
                              {formatPrice(tripBundle.flight.price)}
                            </span>
                          </div>
                          {/* OUT leg */}
                          <div className="text-[10px] text-white/80" style={{ fontFamily: "'Space Mono', monospace" }}>
                            <span className="text-cyan-300 font-bold">OUT:</span>{' '}
                            {tripBundle.flight.outbound?.carrier || ''}{tripBundle.flight.outbound?.flight_number ? ` ${tripBundle.flight.outbound.flight_number}` : ''} | {tripBundle.flight.outbound?.departure_time || '--:--'} → {tripBundle.flight.outbound?.arrival_time || '--:--'}
                          </div>
                          {/* RET leg - MANDATORY */}
                          <div className="text-[10px] text-white/80" style={{ fontFamily: "'Space Mono', monospace" }}>
                            <span className="text-cyan-300 font-bold">RET:</span>{' '}
                            {tripBundle.flight.return?.carrier || ''}{tripBundle.flight.return?.flight_number ? ` ${tripBundle.flight.return.flight_number}` : ''} | {tripBundle.flight.return?.departure_time || '--:--'} → {tripBundle.flight.return?.arrival_time || '--:--'}
                          </div>
                          <div className="text-[10px] text-white/50" style={{ fontFamily: "'Space Mono', monospace" }}>
                            {originIata} ⇄ {CITY_IATA_MAP[event.venue_city?.toLowerCase()] || "---"}
                          </div>
                        </div>
                      ) : (
                        <SkeletonShimmer className="h-6 w-full bg-white/20" />
                      )}
                    </div>
                    
                    {/* Hotel Details - Real data from API */}
                    <div className="p-3 rounded-lg bg-white/10 border border-white/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Hotel className="w-3 h-3 text-white/70" />
                        <span className="text-[10px] text-white/70 uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>
                          Hotel
                        </span>
                      </div>
                      {tripBundle?.hotel ? (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-white font-medium" style={{ fontFamily: "'Space Mono', monospace" }}>
                              {tripBundle.hotel.name || "Loading Hotel Entity..."}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Star Rating */}
                            <div className="flex items-center gap-0.5">
                              {[...Array(tripBundle.hotel.star_rating || 3)].map((_, i) => (
                                <Star key={i} className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400" />
                              ))}
                            </div>
                            <span className="text-[10px] text-white/60" style={{ fontFamily: "'Space Mono', monospace" }}>
                              {tripBundle.hotel.distance_km}km from venue
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-white/60" style={{ fontFamily: "'Space Mono', monospace" }}>
                              {tripBundle.hotel.nights} nights • {tripBundle.hotel.late_checkout ? "Late checkout ✓" : tripBundle.hotel.checkout_time}
                            </span>
                            <span className="text-[10px] text-white/70" style={{ fontFamily: "'Space Mono', monospace" }}>
                              {formatPrice(tripBundle.hotel.price)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <SkeletonShimmer className="h-3 w-full bg-white/20" />
                          <SkeletonShimmer className="h-3 w-2/3 bg-white/20" />
                        </div>
                      )}
                    </div>
                  </div>
                </AccordionReveal>
              </div>
              
              {/* CTA Button - Always at bottom */}
              <div className="p-5 pt-0 mt-auto">
                <Button
                  onClick={() => handleBookClick("completeTrip")}
                  disabled={checkoutLoading || !tripBundle?.flight}
                  className="w-full h-12 bg-white/20 hover:bg-white/30 border border-white/30 text-white backdrop-blur transition-all"
                  data-testid="tier-moonshot-cta"
                >
                  <span style={{ fontFamily: "'Space Mono', monospace" }} className="text-xs uppercase tracking-wider">
                    Select
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Event Details */}
        <div className="grid lg:grid-cols-2 gap-6" style={{ marginTop: '2rem' }}>
          <div className="glass-solid rounded-2xl p-6" style={{ minHeight: '180px', marginBottom: '1rem' }}>
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">When</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Calendar className="w-5 h-5 text-primary" />
                <div>
                  <div className="font-mono text-foreground">{safeFormat(startDate, "EEEE, MMMM dd, yyyy")}</div>
                  <div className="font-mono text-primary">{safeFormat(startDate, "HH:mm")} - {safeFormat(endDate, "HH:mm")}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="glass rounded-2xl p-6">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">Where</h2>
            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 text-accent mt-1" />
              <div>
                <div className="font-heading font-bold text-foreground">{event.venue_name}</div>
                <div className="text-muted-foreground text-sm">{event.venue_address}</div>
                <div className="text-muted-foreground text-sm">{event.venue_city}, {event.venue_country}</div>
              </div>
            </div>
          </div>

          {event.artists?.length > 0 && (
            <div className="glass rounded-2xl p-6 lg:col-span-2">
              <h2 className="font-heading text-xl font-bold text-foreground mb-4">Lineup</h2>
              <div className="flex flex-wrap gap-2">
                {event.artists.map((artist) => (
                  <span key={artist} className="glass px-4 py-2 rounded-full font-mono text-sm text-foreground">
                    {artist}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Stay22 Hotel Map */}
          {event.venue_lat && event.venue_lng && (
            <div className="glass rounded-2xl p-6 lg:col-span-2">
              <h2 className="font-heading text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Hotel className="w-5 h-5 text-accent" />
                Nearby Hotels
              </h2>
              <div className="aspect-video rounded-xl overflow-hidden">
                <iframe
                  src={`https://www.stay22.com/embed/gm?lat=${event.venue_lat}&lng=${event.venue_lng}&zoom=14&maincolor=ff0000&accentcolor=a855f7`}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title="Hotels Map"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />

      {/* Trip Roadmap Overlay */}
      <TripRoadmapOverlay
        isOpen={showOverlay}
        onClose={() => { setShowOverlay(false); setSelectedTier(null); }}
        onProceed={handleProceedToPayment}
        tier={selectedTier}
        event={event}
        origin={originCity}
        guests={guests}
        flight={tripBundle?.flight}
        hotel={tripBundle?.hotel}
        hotelClass={hotelClass}
        totalPrice={selectedTier ? prices[selectedTier]?.total : 0}
        isLoading={checkoutLoading}
      />
    </div>
  );
};

export default EventDetail;